/* JAQUE HUÉ 3D jewelry builder: rings, tennis necklaces, tennis bracelets and stud earrings.
   Price-carrying choices map to real product variants; the rest travel as line item properties. */
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const SHAPE_ICONS = {
  Round: 'M24 6a18 18 0 1 1-0.01 0Z M24 13l7.8 3.2L35 24l-3.2 7.8L24 35l-7.8-3.2L13 24l3.2-7.8Z',
  Oval: 'M24 4C33 4 38 13 38 24S33 44 24 44 10 35 10 24 15 4 24 4Z M24 12L31 24 24 36 17 24Z',
  Princess: 'M8 8H40V40H8Z M8 8L40 40 M40 8L8 40 M16 16H32V32H16Z',
  Emerald: 'M17 4H31L38 11V37L31 44H17L10 37V11Z M19 10H29L32 13V35L29 38H19L16 35V13Z',
  Pear: 'M24 4C24 4 38 20 38 30A14 14 0 0 1 10 30C10 20 24 4 24 4Z M24 14L31 29 24 38 17 29Z',
  Heart: 'M24 42C24 42 6 30 6 17A9 9 0 0 1 24 12A9 9 0 0 1 42 17C42 30 24 42 24 42Z M24 18L33 22 24 35 15 22Z',
  Cushion: 'M15 6H33Q42 6 42 15V33Q42 42 33 42H15Q6 42 6 33V15Q6 6 15 6Z M24 13L35 24 24 35 13 24Z',
  Radiant: 'M15 4H33L38 9V39L33 44H15L10 39V9Z M15 4L24 24 33 4 M15 44L24 24 33 44 M10 9L24 24 10 39 M38 9L24 24 38 39',
  Asscher: 'M16 6H32L42 16V32L32 42H16L6 32V16Z M18 12H30L36 18V30L30 36H18L12 30V18Z M21 18H27L30 21V27L27 30H21L18 27V21Z',
  Baguette: 'M17 4H31V44H17Z M20 9H28V39H20Z',
  Trillion: 'M24 5Q31 18 42 38Q24 42 6 38Q17 18 24 5Z M24 15L33 33H15Z',
  'Elongated cushion': 'M17 4H31Q38 4 38 11V37Q38 44 31 44H17Q10 44 10 37V11Q10 4 17 4Z M24 12L31 24 24 36 17 24Z',
  Marquise: 'M24 3C34 13 34 35 24 45C14 35 14 13 24 3Z M24 13L29 24 24 35 19 24Z',
};
const RING_SHAPES = ['Round', 'Oval', 'Cushion', 'Elongated cushion', 'Princess', 'Asscher', 'Emerald', 'Radiant', 'Pear', 'Marquise', 'Heart', 'Trillion', 'Baguette'];
// every builder offers the full set of classic diamond shapes (specialty cuts like half moons are left out)
const ALL_SHAPES = RING_SHAPES;
const COVERAGE = [{ id: 'full', name: 'Full eternity', sub: 'Diamonds all the way around' }, { id: 'half', name: 'Half eternity', sub: 'Diamonds across the top, can be resized' }];
const STUD_SETTINGS = [{ id: 'prong', name: 'Classic', sub: 'Prongs only, the diamond shows fully' }, { id: 'halo', name: 'Halo', sub: 'Small diamonds frame the stone' }];
const TENNIS_PRONGS = [{ id: 'four', name: '4-prong', sub: 'Classic square of prongs' }, { id: 'three', name: '3-prong', sub: 'Triangle prongs, more diamond shows' }];
const BACKS = [{ id: 'push', name: 'Push backs', sub: 'Quick to put on' }, { id: 'screw', name: 'Screw backs', sub: 'Extra secure' }];
const SETTINGS = [
  { id: 'solitaire', name: 'Solitaire', note: 'A single center diamond on a plain, polished band.', d: 'M12 32a12 12 0 1 0 24 0a12 12 0 1 0 -24 0Z M19 6h10l4 5-9 9-9-9Z M15 11h18' },
  { id: 'pave', name: 'Pavé', note: 'Small diamonds set along the top of the band, leading to the center stone.', d: 'M12 32a12 12 0 1 0 24 0a12 12 0 1 0 -24 0Z M19 6h10l4 5-9 9-9-9Z M15 11h18 M13.5 25.5l1.5 1.5 M12 31h2 M13.5 37l1.5-1.5 M34.5 25.5l-1.5 1.5 M36 31h-2 M34.5 37l-1.5-1.5' },
  { id: 'hidden', name: 'Hidden halo', variant: 'Hidden halo', note: 'A ring of small diamonds tucked under the center stone, seen from the side.', d: 'M12 32a12 12 0 1 0 24 0a12 12 0 1 0 -24 0Z M19 6h10l4 5-9 9-9-9Z M15 11h18 M16 16h16' },
  { id: 'halo', name: 'Halo', note: 'A frame of small diamonds around the center stone makes it look larger.', d: 'M12 34a12 12 0 1 0 24 0a12 12 0 1 0 -24 0Z M14 12a10 8 0 1 0 20 0a10 8 0 1 0 -20 0Z M20 8h8l3 4-7 7-7-7Z' },
  { id: 'threestone', name: 'Three-stone', note: 'Two side diamonds frame the center stone: past, present and future.', d: 'M12 32a12 12 0 1 0 24 0a12 12 0 1 0 -24 0Z M19 6h10l4 5-9 9-9-9Z M3 13h6l2 3-5 5-5-5Z M39 13h6l2 3-5 5-5-5Z' },
  { id: 'vine', name: 'Vine', note: 'A slim band with a fine vine curling over each shoulder, set with marquise-cut leaf diamonds.', d: 'M12 32a12 12 0 1 0 24 0a12 12 0 1 0 -24 0Z M19 6h10l4 5-9 9-9-9Z M4 15c3-4 6-4 8-1c-3 2-6 3-8 1Z M44 15c-3-4-6-4-8-1c3 2 6 3 8 1Z' },
  { id: 'bridal', name: 'Double halo', note: 'Two halos around the center stone, a scalloped outer row of larger diamonds, on a wide band of three wavy pavé rows. Stunning with an oval.', d: 'halo', name: 'Halo', note: 'A frame of small diamonds around the center stone makes it look larger.', d: 'M12 34a12 12 0 1 0 24 0a12 12 0 1 0 -24 0Z M14 12a10 8 0 1 0 20 0a10 8 0 1 0 -20 0Z M20 8h8l3 4-7 7-7-7Z' },
  { id: 'crownset', name: 'Crown set · Limited', variant: 'Crown set', note: 'Limited edition, 2 rings in 1: a crown of three diamonds over the center stone with gold leaves and milgrain, plus a matching contoured band that nests beneath it. Made for a pear center.', d: 'vine', name: 'Vine', note: 'A slim band with a fine vine curling over each shoulder, set with marquise-cut leaf diamonds.', d: 'M12 32a12 12 0 1 0 24 0a12 12 0 1 0 -24 0Z M19 6h10l4 5-9 9-9-9Z M4 15c3-4 6-4 8-1c-3 2-6 3-8 1Z M44 15c-3-4-6-4-8-1c3 2 6 3 8 1Z' },
  { id: 'twist', name: 'Twisted leaf', note: 'Two pavé bands weave into an infinity twist with marquise diamonds in the openings, and a cluster of marquise petals beneath the center stone.', d: 'vine', name: 'Vine', note: 'A slim band with a fine vine curling over each shoulder, set with marquise-cut leaf diamonds.', d: 'M12 32a12 12 0 1 0 24 0a12 12 0 1 0 -24 0Z M19 6h10l4 5-9 9-9-9Z M4 15c3-4 6-4 8-1c-3 2-6 3-8 1Z M44 15c-3-4-6-4-8-1c3 2 6 3 8 1Z' },
  { id: 'grand', name: 'Grand pavé', note: 'A wide statement band with three rows of diamonds: fine pavé along both edges and larger round stones down the middle, plus bezel accents under the center stone. Made for an oval.', d: 'halo', name: 'Halo', note: 'A frame of small diamonds around the center stone makes it look larger.', d: 'M12 34a12 12 0 1 0 24 0a12 12 0 1 0 -24 0Z M14 12a10 8 0 1 0 20 0a10 8 0 1 0 -20 0Z M20 8h8l3 4-7 7-7-7Z' },
];
const COLORS = [
  { id: 'white', name: 'Silver', fill: 'linear-gradient(135deg, #f4f5f7 0%, #c3c7cd 45%, #eef0f2 60%, #a9aeb6 100%)' },
  { id: 'yellow', name: 'Yellow', fill: '#E3BC72' },
  { id: 'rose', name: 'Rose', fill: '#E0A98F' },
];
const MATERIALS = [
  { id: 'silver', name: 'Sterling silver', sub: 'Silver or gold plated', option: 'Gold-plated silver' },
  { id: 'gold', name: 'Solid 14k gold', sub: 'Lasts a lifetime', option: 'Solid 14k gold' },
];
const WIDTHS = [{ id: 'petite', name: 'Petite', mm: 1.8 }, { id: 'classic', name: 'Classic', mm: 2.2 }, { id: 'bold', name: 'Bold', mm: 2.8 }];
const PRONGS = [{ id: 'round', name: 'Classic', sub: 'Rounded tips' }, { id: 'claw', name: 'Claw', sub: 'Pointed tips' }, { id: 'double', name: 'Double', sub: 'Paired prongs' }];
const PROFILES = [{ id: 'low', name: 'Low set', sub: 'Sits close to the finger' }, { id: 'cathedral', name: 'Cathedral', sub: 'Arched shoulders, lifted' }];
const BANDS = [{ id: 'comfort', name: 'Comfort', sub: 'Softly rounded' }, { id: 'knife', name: 'Knife edge', sub: 'Fine center ridge' }, { id: 'flat', name: 'Flat', sub: 'Crisp, modern edges' }];
const STATE_KEYS = ['setting', 'shape', 'carat', 'material', 'color', 'width', 'size', 'stone', 'length', 'nlength', 'prong', 'profile', 'band', 'coverage', 'studset', 'backs', 'tprong'];
const UPLOADS = 'https://jaque-hue-uploads.rs-vip-gifts.workers.dev/upload';
const SIZED = { ring: true, eternity: true };

const find = (list, id) => list.find((x) => x.id === id) || list[0];
/** Plain-words metal: the Silver color is sterling silver, or 14k white gold when solid gold is chosen. */
const metalName = (s) => {
  const silver = s.color === 'white', col = find(COLORS, s.color).name.toLowerCase();
  if (s.material === 'gold') return silver ? '14k white gold' : `14k ${col} gold`;
  return silver ? 'sterling silver' : `sterling silver, ${col} gold plated`;
};
const metalOpt = (s) => find(MATERIALS, s.material).option;

/** Builder state -> variant option values, in the product's option order. */
const VARIANT_MAP = {
  ring: [(s) => find(SETTINGS, s.setting).variant || find(SETTINGS, s.setting).name, (s) => `${s.carat} ct`, metalOpt],
  eternity: [(s) => `${s.stone} mm`, metalOpt, (s) => find(COVERAGE, s.coverage).name],
  tennisNecklace: [(s) => `${s.stone} mm`, (s) => `${s.nlength} in`, metalOpt],
  tennisBracelet: [(s) => `${s.stone} mm`, (s) => `${s.length} in`, metalOpt],
  studs: [(s) => `${s.carat} ct`, metalOpt, (s) => find(STUD_SETTINGS, s.studset).name],
};

const KIND_DEFAULTS = {
  ring: { setting: 'solitaire', shape: 'Round', carat: '0.5', material: 'silver', color: 'yellow', width: 'classic', prong: 'round', profile: 'low', band: 'comfort' },
  eternity: { shape: 'Round', stone: '2', material: 'silver', color: 'yellow', coverage: 'half' },
  tennisNecklace: { stone: '2', nlength: '16', material: 'silver', color: 'yellow', tprong: 'four' },
  tennisBracelet: { stone: '2', length: '6', material: 'silver', color: 'yellow', tprong: 'four' },
  studs: { shape: 'Round', carat: '0.25', material: 'silver', color: 'yellow', studset: 'prong', backs: 'push' },
};

const icon = (d, size) =>
  `<svg width="${size}" height="${size}" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;
const sub = (names) => (v, unit) => `<span>${v} ${unit}</span><small>${names[v]}</small>`;

class CjBuilder extends HTMLElement {
  connectedCallback() {
    if (this.config) return;
    this.config = JSON.parse(this.querySelector('script[data-config]').textContent);
    this.kind = this.config.kind in VARIANT_MAP ? this.config.kind : 'ring';
    this.state = Object.assign(
      { setting: 'solitaire', shape: 'Round', carat: '1', material: 'gold', color: 'white', width: 'classic', size: '', engraving: '', clasp: '', stone: '3', length: '7', nlength: '16', prong: 'round', profile: 'low', band: 'comfort', coverage: 'full', studset: 'prong', backs: 'push', tprong: 'four' },
      KIND_DEFAULTS[this.kind],
      this.config.defaults || {},
      this.#paramsFromUrl()
    );
    this.stepFns = [];
    this.dims = () => '';
    this.box = { include: true, values: null, visited: false };
    // Optional paid add-on: the engraved walnut rest tray.
    this.tray = { include: false, values: null, visited: false };
    // The Keepsake Studio snippet may finish loading after the builder; show its step as soon as it is ready.
    window.addEventListener('cj:keepsake-ready', () => this.#refresh());
    this.#buildSteps();
    this.#bindStage();
    this.#refresh();
    this.querySelector('[data-add]').addEventListener('click', () => this.#addToCart());
    this.#restoreFromCart();
    this.querySelector('[data-photos]')?.addEventListener('click', () => this.#openPhotos());
    this.querySelector('[data-hand-photo]')?.addEventListener('click', () => this.#openHand());

    window.CJReady?.then(() => this.#startViewer()).catch(() => this.#stageFailed());
  }

  async #exportGlb() {
    const T = window.THREE, src = this.viewer && this.viewer.model && this.viewer.model(); if (!T || !src) return null;
    if (!T.GLTFExporter) await new Promise((res, rej) => { const sc = document.createElement('script'); sc.src = 'https://cdn.jsdelivr.net/npm/three@0.147.0/examples/js/exporters/GLTFExporter.js'; sc.onload = res; sc.onerror = rej; document.head.appendChild(sc); });
    // export the solid piece only: metal and stones, no sparkle sprites or facet lines
    const out = new T.Group(); src.updateMatrixWorld(true);
    src.traverse((o) => { if (!o.isMesh || o.isInstancedMesh === undefined && false) return; if (!o.isMesh || o.userData.isCrown || o.userData.noExport || !o.visible) return;
      const geos = []; if (o.isInstancedMesh) { const m4 = new T.Matrix4(); for (let i = 0; i < o.count; i++) { o.getMatrixAt(i, m4); const g = o.geometry.clone(); g.applyMatrix4(m4); geos.push(g); } } else geos.push(o.geometry.clone());
      for (const g of geos) { g.applyMatrix4(o.matrixWorld); const isStone = !!(o.material && ((o.material.userData && o.material.userData.isDiamond) || (o.material.metalness === 1 && o.material.roughness === 0 && o.material.side === T.BackSide)));
        const mat = new T.MeshStandardMaterial({ color: isStone ? 0xf4f7fb : o.material.color, metalness: isStone ? 0.1 : 1, roughness: isStone ? 0.05 : 0.18, transparent: isStone, opacity: isStone ? 0.85 : 1 });
        const m = new T.Mesh(g, mat); m.name = isStone ? 'diamond' : 'metal'; out.add(m); } });
    if (!out.children.length) return null;
    return await new Promise((res) => new T.GLTFExporter().parse(out, (r) => res(r), () => res(null), { binary: true }));
  }

  async #restoreFromCart() {
    const key = new URLSearchParams(location.search).get('edit'); if (!key) return;
    try {
      const cart = await (await fetch('/cart.js')).json(); const line = cart.items.find((i) => i.key === key); if (!line || !line.properties || !line.properties._design) return;
      const d = JSON.parse(line.properties._design); this.editKey = key; this.editGroup = line.properties._g || '';
      Object.assign(this.state, d.s || {});
      if (d.box) Object.assign(this.box, d.box, { visited: true }); if (d.tray) Object.assign(this.tray, d.tray, { visited: true });
      this.#buildSteps(); this.#refresh(); if (this.viewer) this.viewer.set(this.kind, this.state);
      const btn = this.querySelector('[data-add]'); this.#refresh();
      const note = document.createElement('p'); note.className = 'cj-editnote'; note.textContent = 'Editing the design in your bag. Change anything, then update it.'; btn?.insertAdjacentElement('beforebegin', note);
    } catch (e) { /* start fresh if the bag line is gone */ }
  }

  disconnectedCallback() {
    this.mirrorAbort?.abort();
  }

  #paramsFromUrl() {
    const out = {};
    const params = new URLSearchParams(location.search);
    STATE_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) out[k] = v;
    });
    return out;
  }

  #syncUrl() {
    const params = new URLSearchParams(location.search);
    ['setting', 'shape', 'carat', 'color', 'stone', 'length', 'nlength', 'prong', 'profile', 'band', 'coverage', 'studset', 'backs', 'tprong'].forEach((k) => {
      if (this.#stepKeys().includes(k)) params.set(k, this.state[k]);
    });
    history.replaceState(history.state, '', `${location.pathname}?${params}`);
  }

  #stepKeys() {
    return { ring: ['setting', 'shape', 'carat', 'color', 'prong', 'profile', 'band'], eternity: ['coverage', 'shape', 'stone', 'color'], studs: ['shape', 'carat', 'studset', 'color', 'backs'], tennisNecklace: ['stone', 'nlength', 'tprong', 'color'], tennisBracelet: ['stone', 'length', 'tprong', 'color'] }[this.kind];
  }

  #money(cents) {
    return formatMoney(cents, this.config.moneyFormat, this.config.currency);
  }

  #variantFor(state) {
    const want = VARIANT_MAP[this.kind].map((fn) => fn(state));
    return this.config.variants.find((v) => want.every((val, i) => v.options[i] === val));
  }

  #steps() {
    const k = this.kind, s = [];
    const choice = (key, legend, items, cols, cls, render, label) => s.push({ type: 'choice', key, legend, items, cols, cls, render, label });
    const shape = (v) => icon(SHAPE_ICONS[v], 30) + `<span>${v}</span>`;
    const caratLabel = (v) => `<span>${v} ct</span><small>${this.dims(this.state.shape, v)}</small>`;
    const stoneNames = { 2: 'Delicate', 3: 'Classic', 4: 'Statement' };
    // metal comes first on every design
    s.push({ type: 'metal' });
    if (k === 'ring') {
      choice('setting', 'Setting', SETTINGS.map((x) => x.id), 3, 'cj-opt--tall', (v) => icon(find(SETTINGS, v).d, 46) + `<span>${find(SETTINGS, v).name}</span>`, (v) => find(SETTINGS, v).name);
      choice('shape', 'Center diamond', RING_SHAPES, 3, 'cj-opt--icon', shape, (v) => v);
      choice('carat', 'Carat weight', ['0.5', '1', '1.5', '2', '2.5', '3'], 3, '', caratLabel, (v) => `${v} ct`);
    }
    if (k === 'eternity') {
      choice('coverage', 'Style', COVERAGE.map((x) => x.id), 2, '', (v) => `<span>${find(COVERAGE, v).name}</span><small>${find(COVERAGE, v).sub}</small>`, (v) => find(COVERAGE, v).name);
      choice('shape', 'Diamond shape', ALL_SHAPES, 3, 'cj-opt--icon', shape, (v) => v);
      choice('stone', 'Stone size', ['2', '3', '4'], 3, '', (v) => sub(stoneNames)(v, 'mm'), (v) => `${v} mm`);
    }
    if (k === 'tennisNecklace') {
      choice('shape', 'Diamond shape', ALL_SHAPES, 4, 'cj-opt--icon', shape, (v) => v);
      choice('stone', 'Stone size', ['2', '3', '4'], 3, '', (v) => sub(stoneNames)(v, 'mm'), (v) => `${v} mm`);
      choice('nlength', 'Length', ['16', '18', '20'], 3, '', (v) => sub({ 16: 'At the collarbone', 18: 'Just below it', 20: 'Rests on the chest' })(v, 'in'), (v) => `${v} in`);
      choice('tprong', 'Setting', TENNIS_PRONGS.map((x) => x.id), 2, '', (v) => `<span>${find(TENNIS_PRONGS, v).name}</span><small>${find(TENNIS_PRONGS, v).sub}</small>`, (v) => find(TENNIS_PRONGS, v).name);
      s.push({ type: 'clasp' });
    }
    if (k === 'tennisBracelet') {
      choice('shape', 'Diamond shape', ALL_SHAPES, 4, 'cj-opt--icon', shape, (v) => v);
      choice('stone', 'Stone size', ['2', '3', '4'], 3, '', (v) => sub(stoneNames)(v, 'mm'), (v) => `${v} mm`);
      choice('length', 'Length', ['6', '6.5', '7', '7.5', '8'], 5, '', (v) => sub({ 6: 'Petite wrist', 6.5: 'Small wrist', 7: 'Most women ★', 7.5: 'Most men ★', 8: 'Large wrist' })(v, 'in'), (v) => `${v} in`);
      choice('tprong', 'Setting', TENNIS_PRONGS.map((x) => x.id), 2, '', (v) => `<span>${find(TENNIS_PRONGS, v).name}</span><small>${find(TENNIS_PRONGS, v).sub}</small>`, (v) => find(TENNIS_PRONGS, v).name);
      s.push({ type: 'clasp' });
    }
    if (k === 'studs') {
      choice('shape', 'Diamond shape', ALL_SHAPES, 3, 'cj-opt--icon', shape, (v) => v);
      choice('carat', 'Carat weight, each', ['0.25', '0.5', '0.75', '1', '1.5', '2'], 3, '', caratLabel, (v) => `${v} ct each`);
      choice('studset', 'Setting', STUD_SETTINGS.map((x) => x.id), 2, '', (v) => `<span>${find(STUD_SETTINGS, v).name}</span><small>${find(STUD_SETTINGS, v).sub}</small>`, (v) => find(STUD_SETTINGS, v).name);
    }
    if (k === 'ring') {
      choice('prong', 'Prongs', PRONGS.map((x) => x.id), 3, '', (v) => `<span>${find(PRONGS, v).name}</span><small>${find(PRONGS, v).sub}</small>`, (v) => find(PRONGS, v).name);
      choice('profile', 'Profile', PROFILES.map((x) => x.id), 2, '', (v) => `<span>${find(PROFILES, v).name}</span><small>${find(PROFILES, v).sub}</small>`, (v) => find(PROFILES, v).name);
      choice('band', 'Band style', BANDS.map((x) => x.id), 3, '', (v) => `<span>${find(BANDS, v).name}</span><small>${find(BANDS, v).sub}</small>`, (v) => find(BANDS, v).name);
      choice('width', 'Band width', WIDTHS.map((w) => w.id), 3, '', (v) => `<span>${find(WIDTHS, v).name}</span><small>${find(WIDTHS, v).mm} mm</small>`, (v) => `${find(WIDTHS, v).mm} mm`);
    }
    if (k === 'studs') choice('backs', 'Earring backs', BACKS.map((x) => x.id), 2, '', (v) => `<span>${find(BACKS, v).name}</span><small>${find(BACKS, v).sub}</small>`, (v) => find(BACKS, v).name);
    if (SIZED[k]) s.push({ type: 'size' });
    s.push({ type: 'keepsake' });
    s.push({ type: 'tray' });
    return s;
  }

  #buildSteps() {
    const box = this.querySelector('[data-steps]');
    box.innerHTML = '';
    this.stepFns = [];
    this.#steps().forEach((st, i) => {
      const fs = document.createElement('fieldset');
      fs.className = 'cj-step';
      const num = `<span class="cj-step__num">${i + 1}</span>`;
      if (st.type === 'choice') {
        fs.innerHTML = `<legend class="cj-step__legend">${num}<span class="cj-step__title">${st.legend}</span><span class="cj-step__value"></span></legend><div class="cj-opts cj-opts--${st.cols}"></div>` + (st.key === 'setting' ? '<p class="cj-note" data-setting-note></p>' : '');
        if (st.key === 'length') fs.insertAdjacentHTML('beforeend', '<p class="cj-tip">Not sure? Most women wear <b>7 in</b> and most men <b>7.5 in</b>. Measure your wrist snugly and add about half an inch.</p>');
        const btns = st.items.map((v) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = `cj-opt ${st.cls}`;
          b.addEventListener('click', () => { this.state[st.key] = v; this.#refresh(true); });
          fs.querySelector('.cj-opts').appendChild(b);
          return { v, el: b };
        });
        this.stepFns.push(() => {
          fs.querySelector('.cj-step__value').textContent = st.label(this.state[st.key]);
          btns.forEach((b) => { b.el.innerHTML = st.render(b.v); b.el.setAttribute('aria-pressed', String(b.v === this.state[st.key])); });
          const note = fs.querySelector('[data-setting-note]');
          if (note) note.textContent = find(SETTINGS, this.state.setting).note;
        });
      } else if (st.type === 'metal') {
        fs.innerHTML = `<legend class="cj-step__legend">${num}<span class="cj-step__title">Metal</span><span class="cj-step__value"></span></legend><div class="cj-opts cj-opts--2"></div><div class="cj-swatches" role="group" aria-label="Metal color"></div>`;
        const mBtns = MATERIALS.map((m) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'cj-opt cj-opt--left';
          b.addEventListener('click', () => { this.state.material = m.id; this.#refresh(true); });
          fs.querySelector('.cj-opts').appendChild(b);
          return { m, el: b };
        });
        const cBtns = COLORS.map((c) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'cj-swatch';
          b.innerHTML = `<i style="background:${c.fill}"></i><span>${c.name}</span>`;
          b.addEventListener('click', () => { this.state.color = c.id; this.#refresh(true); });
          fs.querySelector('.cj-swatches').appendChild(b);
          return { c, el: b };
        });
        this.stepFns.push(() => {
          const col = find(COLORS, this.state.color).name;
          fs.querySelector('.cj-step__value').textContent = this.state.material === 'gold' ? (this.state.color === 'white' ? '14k white gold' : `14k ${col.toLowerCase()} gold`) : (this.state.color === 'white' ? 'Sterling silver' : `${col} gold-plated silver`);
          const here = this.#variantFor(this.state);
          mBtns.forEach((b) => {
            const other = this.#variantFor(Object.assign({}, this.state, { material: b.m.id }));
            const d = here && other ? other.price - here.price : 0;
            b.el.innerHTML = `<span>${b.m.name}</span><small>${b.m.sub}${d > 0 ? `, +${this.#money(d)}` : d < 0 ? `, −${this.#money(-d)}` : ''}</small>`;
            b.el.setAttribute('aria-pressed', String(b.m.id === this.state.material));
          });
          cBtns.forEach((b) => b.el.setAttribute('aria-pressed', String(b.c.id === this.state.color)));
        });
      } else if (st.type === 'tray') {
        fs.innerHTML =
          `<legend class="cj-step__legend">${num}<span class="cj-step__title">Engraved rest tray</span><span class="cj-step__value"></span></legend>` +
          '<div class="cj-keep"><img class="cj-keep__img" data-tray-img alt="" width="96" height="96" loading="lazy">' +
          '<div class="cj-keep__body"><p class="cj-keep__head">A walnut ring dish engraved with your names <span data-tray-price></span></p>' +
          '<p class="cj-keep__sum" data-tray-sum></p>' +
          `<label class="cj-keep__inc"><input type="checkbox" data-tray-include> Add an engraved rest tray</label>` +
          '<button type="button" class="button button-secondary cj-keep__open" data-tray-open>Personalize the tray</button></div></div>';
        const inc = fs.querySelector('[data-tray-include]');
        fs.querySelector('[data-tray-open]').addEventListener('click', () => { this.tray.include = true; inc.checked = true; if (this.wizShow && document.body.classList.contains('jq-lab')) this.wizShow(this.wizStep); else this.#openTray(); });
        inc.addEventListener('change', () => { this.tray.include = inc.checked; this.#refresh(); if (inc.checked && this.wizShow && document.body.classList.contains('jq-lab')) this.wizShow(this.wizStep); else if (inc.checked && !this.tray.visited) this.#openTray(); });
        this.stepFns.push(() => {
          const T = window.CJKeepsake?.items?.tray;
          fs.closest('.cj-step-panel')?.toggleAttribute('hidden', !T);
          if (!T) return;
          const img = fs.querySelector('[data-tray-img]');
          if (!img.getAttribute('src')) img.src = T.image;
          fs.querySelector('[data-tray-price]').textContent = `(+${this.#money(T.price)})`;
          fs.querySelector('[data-tray-sum]').textContent = !this.tray.include ? 'Not added' : this.tray.values ? window.CJKeepsake.summary(this.tray.values) : 'Added, personalize the engraving';
          fs.querySelector('.cj-step__value').textContent = this.tray.include ? `+${this.#money(T.price)}` : 'Not added';
          inc.checked = this.tray.include;
        });
      } else if (st.type === 'clasp') {
        // Two engraved letters on the face of the box clasp (optional).
        const id = this.id || 'cj';
        fs.innerHTML =
          `<legend class="cj-step__legend">${num}<span class="cj-step__title">Clasp engraving</span><span class="cj-step__value"></span></legend>` +
          `<div class="cj-field"><div class="cj-field__head"><label for="${id}-clasp">Two letters on the clasp (optional)</label><span data-ccount>0/2</span></div>` +
          `<input id="${id}-clasp" type="text" maxlength="2" autocomplete="off" autocapitalize="characters" placeholder="e.g. JH" style="text-transform:uppercase"></div>` +
          '<p class="cj-note">Engraved free on the inside of the clasp. The outside carries the JH hallmark.</p>';
        const inp = fs.querySelector('input');
        inp.value = this.state.clasp || '';
        inp.addEventListener('input', () => {
          const v = inp.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
          if (inp.value !== v) inp.value = v;
          this.state.clasp = v; this.#refresh(); this.#showClasp();
        });
        this.stepFns.push(() => {
          fs.querySelector('[data-ccount]').textContent = `${(this.state.clasp || '').length}/2`;
          fs.querySelector('.cj-step__value').textContent = this.state.clasp ? `“${this.state.clasp}”` : 'None';
        });
      } else if (st.type === 'keepsake') {
        fs.innerHTML =
          `<legend class="cj-step__legend">${num}<span class="cj-step__title">Keepsake box</span><span class="cj-step__value"></span></legend>` +
          '<div class="cj-keep"><img class="cj-keep__img" data-keep-img alt="" width="96" height="96" loading="lazy">' +
          '<div class="cj-keep__body"><p class="cj-keep__head">Handcrafted walnut box with free engraving <span data-keep-value></span></p>' +
          '<p class="cj-keep__sum" data-keep-sum></p>' +
          '<button type="button" class="button button-secondary cj-keep__open" data-keep-open>Open Keepsake Studio</button>' +
          `<label class="cj-keep__inc"><input type="checkbox" data-keep-include checked> Include my free keepsake box</label></div></div>`;
        fs.querySelector('[data-keep-open]').addEventListener('click', () => { if (this.wizShow && document.body.classList.contains('jq-lab')) { this.box.include = true; this.wizShow(this.wizStep); } else this.#openKeepsake(); });
        fs.querySelector('[data-keep-include]').addEventListener('change', (e) => { this.box.include = e.target.checked; this.#refresh(); });
        this.stepFns.push(() => {
          const K = window.CJKeepsake;
          fs.closest('.cj-step-panel')?.toggleAttribute('hidden', !K);
          if (!K) return;
          const img = fs.querySelector('[data-keep-img]');
          if (!img.getAttribute('src')) img.src = K.lidImage;
          fs.querySelector('[data-keep-value]').textContent = K.value ? `(${K.value} value, free)` : '(free)';
          const sum = !this.box.include ? 'Not included' : this.box.values ? K.summary(this.box.values) : 'Free, add an engraving';
          fs.querySelector('[data-keep-sum]').textContent = sum;
          fs.querySelector('.cj-step__value').textContent = this.box.include ? 'Free' : 'Not included';
          fs.querySelector('[data-keep-open]').disabled = !this.box.include;
        });
      } else {
        let opts = '';
        for (let v = 3; v <= 13; v += 0.5) opts += `<option value="${v}">${v} (${(11.63 + 0.8128 * v).toFixed(1)} mm inside)</option>`;
        const resizeNote = '<p class="cj-note" data-resize-note></p>';
        const guide = this.config.sizeGuideUrl ? `<a href="${this.config.sizeGuideUrl}">Size guide</a>` : '';
        const id = this.id || 'cj';
        fs.innerHTML =
          `<legend class="cj-step__legend">${num}<span class="cj-step__title">Size and engraving</span></legend>` +
          `<div class="cj-field"><div class="cj-field__head"><label for="${id}-size">Ring size</label>${guide}</div>` +
          `<select id="${id}-size" aria-describedby="${id}-size-err"><option value="">Select a size</option>${opts}</select>` +
          `${resizeNote}<p class="cj-error" id="${id}-size-err" hidden>Choose a ring size to add this to your bag.</p></div>` +
          `<div class="cj-field"><div class="cj-field__head"><label for="${id}-engrave">Engraving inside the band (optional)</label><span data-ecount>0/25</span></div>` +
          `<input id="${id}-engrave" type="text" maxlength="25" autocomplete="off" placeholder="Initials and a date"></div>`;
        const sel = fs.querySelector('select'), err = fs.querySelector('.cj-error'), eng = fs.querySelector('input');
        sel.value = this.state.size;
        eng.value = this.state.engraving;
        sel.addEventListener('change', () => { this.state.size = sel.value; err.hidden = true; sel.classList.remove('is-invalid'); this.#refresh(); });
        eng.addEventListener('input', () => { this.state.engraving = eng.value.slice(0, 25); this.#refresh(); this.#showEngraving(); });
        this.stepFns.push(() => {
          fs.querySelector('[data-ecount]').textContent = `${this.state.engraving.length}/25`;
          fs.querySelector('[data-resize-note]').textContent = this.kind === 'eternity' && this.state.coverage !== 'half' ? 'Full eternity bands can’t be resized, so please confirm your size.' : 'One free resize in the first year.';
        });
        this.sizeSelect = sel;
        this.sizeError = err;
      }
      const panel = document.createElement('details');
      panel.className = 'cj-step-panel';
      if (i === 0) panel.open = true;
      const summary = document.createElement('summary');
      summary.className = 'cj-step-panel__head';
      const legend = fs.querySelector('legend');
      summary.innerHTML = legend.innerHTML;
      legend.classList.add('cj-sr');
      panel.append(summary, fs);
      const mirror = summary.querySelector('.cj-step__value');
      const source = legend.querySelector('.cj-step__value');
      if (mirror && source) this.stepFns.push(() => { mirror.textContent = source.textContent; });
      box.appendChild(panel);
    });
    this.#wizard();
  }

  /**
   * Step-by-step flow: a progress row of every step (current choice under each), one step shown at a time with Back /
   * Next, and a final Review step that shows the summary and Add to bag. Steps already seen can be jumped back to.
   */
  #wizard() {
    const box = this.querySelector('[data-steps]'), summary = this.querySelector('.cj-summary');
    this.querySelectorAll('.cj-wiz__nav, .cj-wiz__bar').forEach((el) => el.remove()); // rebuilt steps (editing from the bag)
    const panels = [...box.querySelectorAll(':scope > .cj-step-panel')]; if (!panels.length) return;
    this.classList.add('cj-wiz');
    const names = panels.map((p) => p.querySelector('.cj-step__title')?.textContent.trim() || '');
    const total = panels.length + 1; // + Review
    const nav = document.createElement('ol'); nav.className = 'cj-wiz__nav'; nav.setAttribute('aria-label', 'Design steps');
    [...names, 'Review'].forEach((n, i) => { const li = document.createElement('li'); li.innerHTML = `<button type="button" data-go="${i}"><b>${i + 1}</b><span>${n}</span><small data-wv="${i}"></small></button>`; nav.appendChild(li); });
    const bar = document.createElement('div'); bar.className = 'cj-wiz__bar';
    bar.innerHTML = '<button type="button" class="cj-wiz__back" data-wback>Back</button><span class="cj-wiz__count" data-wcount></span><button type="button" class="button cj-wiz__next" data-wnext>Next</button>';
    box.after(bar); bar.after(nav); nav.classList.add('cj-wiz__trail');
    panels.forEach((p) => { p.open = true; p.querySelector('summary').addEventListener('click', (e) => e.preventDefault()); });
    if (this.wizStep == null) this.wizStep = this.editKey ? total - 1 : 0;
    this.wizSeen = Math.max(this.wizSeen || 0, this.wizStep);
    const show = (i, scroll) => {
      this.wizStep = i = Math.max(0, Math.min(total - 1, i)); this.wizSeen = Math.max(this.wizSeen, i);
      panels.forEach((p, k) => p.classList.toggle('is-wiz-off', k !== i));
      const review = i === total - 1;
      if (summary) summary.hidden = !review;
      this.classList.toggle('is-review', review); // details and stats only on the review step
      bar.querySelector('[data-wback]').hidden = i === 0;
      bar.querySelector('[data-wnext]').hidden = review;
      bar.querySelector('[data-wnext]').textContent = i === total - 2 ? 'Review your design' : `Next: ${names[i + 1]}`;
      bar.querySelector('[data-wcount]').textContent = `Step ${i + 1} of ${total}`;
      const lab = document.body.classList.contains('jq-lab'), cur = panels[i];
      if (lab && cur && !this.wizStudio) {
        const isBox = cur.querySelector('[data-keep-open]'), isTray = cur.querySelector('[data-tray-open]');
        const go = isBox && this.box.include ? () => this.#openKeepsake({ embed: true, goLabel: 'Save and continue', skipLabel: 'Continue without engraving' })
          : isTray && this.tray.include ? () => this.#openTray({ embed: true, goLabel: 'Save and continue', skipLabel: 'Continue without engraving' }) : null;
        if (go) { this.wizStudio = true; go().then((how) => { this.wizStudio = false; show(how === 'back' ? i - 1 : i + 1, true); }); }
      }
      nav.querySelectorAll('button').forEach((b, k) => { b.setAttribute('aria-current', k === i ? 'step' : 'false'); b.classList.toggle('is-done', k < i || (k <= this.wizSeen && k !== i)); b.disabled = k > this.wizSeen + 1; });
      nav.querySelector('[aria-current="step"]')?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      if (scroll) { const top = nav.getBoundingClientRect().top + window.scrollY - 90; if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' }); }
    };
    // the choice made in each step, under its name in the progress row
    // live spec readout on the stage (the lab's bench label)
    this.stepFns.push(() => { const ro = this.querySelector('[data-readout]'); if (ro) ro.textContent = this.#specs().filter((x) => !/box|tray|engraving|size/i.test(x[0])).map((x) => x[1]).join('  ·  ').toUpperCase(); });
    this.stepFns.push(() => panels.forEach((p, k) => { const v = p.querySelector('.cj-step__value'); const el = nav.querySelector(`[data-wv="${k}"]`); if (el) el.textContent = v ? v.textContent : ''; }));
    nav.addEventListener('click', (e) => { const b = e.target.closest('[data-go]'); if (b && !b.disabled) show(+b.dataset.go, true); });
    bar.querySelector('[data-wnext]').addEventListener('click', () => show(this.wizStep + 1, true));
    bar.querySelector('[data-wback]').addEventListener('click', () => show(this.wizStep - 1, true));
    this.wizShow = show;
    show(this.wizStep);
  }

  #specs() {
    const keep = window.CJKeepsake && (this.box.include ? `Free · ${this.box.values ? window.CJKeepsake.summary(this.box.values) : 'No engraving'}` : 'Not included');
    const s = this.state, r = [], col = find(COLORS, s.color).name.toLowerCase();
    const metal = metalName(s).replace(/^./, (c) => c.toUpperCase());
    const dims = (shape, carat) => (this.dims(shape, carat) ? `, about ${this.dims(shape, carat)}` : '');
    if (this.kind === 'ring') {
      const w = find(WIDTHS, s.width);
      r.push(['Setting', find(SETTINGS, s.setting).name], ['Prongs and profile', `${find(PRONGS, s.prong).name} prongs, ${find(PROFILES, s.profile).name.toLowerCase()}`], ['Center stone', `${s.carat} ct ${s.shape.toLowerCase()} lab-grown diamond${dims(s.shape, s.carat)}`], ['Metal', metal], ['Band', `${find(BANDS, s.band).name}, ${w.name.toLowerCase()}, ${w.mm} mm wide`], ['Ring size', s.size || 'Not selected'], ['Engraving', s.engraving ? `“${s.engraving}”` : 'None']);
    }
    if (this.kind === 'eternity') r.push(['Style', find(COVERAGE, s.coverage).name], ['Stones', `${s.stone} mm ${s.shape.toLowerCase()} lab-grown diamonds, ${s.coverage === 'half' ? 'across the top half' : 'all the way around'}`], ['Metal', metal], ['Ring size', s.size || 'Not selected'], ['Engraving', s.engraving ? `“${s.engraving}”` : 'None']);
    if (this.kind === 'tennisNecklace') r.push(['Stones', `${s.stone} mm ${s.shape.toLowerCase()} lab-grown diamonds`], ['Setting', find(TENNIS_PRONGS, s.tprong).name], ['Length', `${s.nlength} in`], ['Metal', metal], ['Clasp engraving', s.clasp ? `“${s.clasp}”` : 'None']);
    if (this.kind === 'tennisBracelet') r.push(['Stones', `${s.stone} mm ${s.shape.toLowerCase()} lab-grown diamonds`], ['Setting', find(TENNIS_PRONGS, s.tprong).name], ['Length', `${s.length} in`], ['Metal', metal], ['Clasp engraving', s.clasp ? `“${s.clasp}”` : 'None']);
    if (this.kind === 'studs') r.push(['Stones', `Two ${s.carat} ct ${s.shape.toLowerCase()} lab-grown diamonds${dims(s.shape, s.carat)}`], ['Setting', find(STUD_SETTINGS, s.studset).name], ['Metal', metal], ['Backs', find(BACKS, s.backs).name]);
    if (keep) r.push(['Keepsake box', keep]);
    const T = window.CJKeepsake?.items?.tray;
    if (T && this.tray.include) r.push(['Rest tray', `${this.#money(T.price)} · ${this.tray.values ? window.CJKeepsake.summary(this.tray.values) : 'No engraving yet'}`]);
    return r;
  }

  #properties() {
    const s = this.state, p = {};
    const col = find(COLORS, s.color).name;
    // first line on the order: which metal was bought
    p['Metal'] = s.material === 'gold' ? 'Solid 14k gold' : 'Sterling silver';
    p['Diamond shape'] = s.shape;
    p['Metal color'] = s.color === 'white' ? (s.material === 'gold' ? 'Silver (white gold)' : 'Silver') : s.material === 'gold' ? `${col} gold` : `${col} gold plating`;
    if (this.kind === 'ring') {
      p['Prongs'] = find(PRONGS, s.prong).name;
      p['Profile'] = find(PROFILES, s.profile).name;
      if (s.setting === 'vine') p['Band style'] = 'Twisted vine';
      else { p['Band style'] = find(BANDS, s.band).name; p['Band width'] = `${find(WIDTHS, s.width).name} (${find(WIDTHS, s.width).mm} mm)`; }
    }
    if (this.kind === 'studs') p['Backs'] = find(BACKS, s.backs).name;
    if (this.kind === 'tennisNecklace' || this.kind === 'tennisBracelet') p['Setting'] = find(TENNIS_PRONGS, s.tprong).name;
    if ((this.kind === 'tennisBracelet' || this.kind === 'tennisNecklace') && s.clasp) p['Clasp engraving'] = s.clasp;
    if (SIZED[this.kind]) {
      p['Ring size'] = s.size;
      if (s.engraving) p['Engraving'] = s.engraving;
    }
    return p;
  }

  #refresh(fromUser) {
    this.stepFns.forEach((f) => f());
    const variant = this.#variantFor(this.state);
    this.variant = variant;
    const trayItem = this.tray.include ? window.CJKeepsake?.items?.tray : null;
    const priceText = variant ? this.#money(variant.price + (trayItem ? trayItem.price : 0)) : 'Unavailable';
    this.querySelectorAll('[data-price]').forEach((el) => (el.textContent = priceText));
    // Shop Pay: 4 interest-free installments on $75 and up
    const total = variant ? variant.price + (trayItem ? trayItem.price : 0) : 0;
    this.querySelectorAll('[data-inst]').forEach((el) => { el.hidden = total < 7500; const a = el.querySelector('[data-inst-amt]'); if (a) a.textContent = this.#money(Math.round(total / 4)); });
    const add = this.querySelector('[data-add]');
    const ok = Boolean(variant && variant.available);
    add.disabled = !ok;
    add.querySelector('[data-add-label]').textContent = ok ? (this.editKey ? 'Update my design' : this.config.addLabel) : 'Unavailable';
    const realBtn = this.querySelector('[data-hand-photo]');
    if (realBtn) realBtn.hidden = !this.#realPhotos().length;
    const hint = this.querySelector('[data-unavailable]');
    if (hint) hint.hidden = ok;
    if (this.viewer) this.#renderStats(this.viewer.stats);
    const dl = this.querySelector('[data-specs]');
    dl.innerHTML = '';
    this.#specs().forEach(([k, v]) => {
      const dt = document.createElement('dt'), dd = document.createElement('dd');
      dt.textContent = k; dd.textContent = v;
      dl.append(dt, dd);
    });
    const canvas = this.querySelector('[data-canvas]');
    canvas.setAttribute('aria-label', `3D preview: ${this.#specs().slice(0, 2).map((r) => r[1]).join(', ')}`);
    if (this.viewer) this.viewer.set(this.kind, this.state);
    if (fromUser) this.#syncUrl();
  }

  /**
   * Phones and tablets: once the 3D stage scrolls away, a small live copy of it sticks to the top left.
   * It hides again when the stage is on screen, or while the Add to bag button passes under it, so it never covers the cart.
   * It copies the main viewer's frames, so no second WebGL context is created.
   */
  #mountMirror() {
    const stage = this.querySelector('.cj-stage'), add = this.querySelector('[data-add]');
    if (!stage || this.mirror) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cj-mirror';
    btn.setAttribute('aria-label', 'Back to the 3D preview');
    btn.innerHTML = '<canvas width="420" height="420" aria-hidden="true"></canvas><span class="cj-mirror__price" data-price></span><span class="cj-mirror__grip" aria-hidden="true"></span>';
    // drag the bottom-right corner to make the mirror bigger (remembered on this device)
    try { const sv = +localStorage.getItem('cj-mirror-size'); if (sv) btn.style.setProperty('--cj-m', sv + 'px'); } catch (e) { }
    let resized = false;
    btn.querySelector('.cj-mirror__grip').addEventListener('pointerdown', (e) => {
      e.preventDefault(); e.stopPropagation(); const g = e.currentTarget; try { g.setPointerCapture(e.pointerId); } catch (err) { }
      const r = btn.getBoundingClientRect(), x0 = e.clientX, w0 = r.width - 2; resized = false;
      const max = Math.min(window.innerWidth - r.left - 8, window.innerHeight * 0.6);
      const move = (ev) => { const w = Math.max(84, Math.min(max, w0 + (ev.clientX - x0))); if (Math.abs(ev.clientX - x0) > 3) resized = true; btn.style.setProperty('--cj-m', Math.round(w) + 'px'); };
      const up = () => { g.removeEventListener('pointermove', move); g.removeEventListener('pointerup', up); g.removeEventListener('pointercancel', up); try { localStorage.setItem('cj-mirror-size', parseInt(btn.style.getPropertyValue('--cj-m')) || 84); } catch (e) { } setTimeout(() => (resized = false), 50); };
      g.addEventListener('pointermove', move); g.addEventListener('pointerup', up); g.addEventListener('pointercancel', up);
    });
    btn.querySelector('[data-price]').textContent = this.querySelector('[data-price]')?.textContent || '';
    this.appendChild(btn);
    this.mirror = btn;
    const ctx = btn.querySelector('canvas').getContext('2d');
    const mq = window.matchMedia('(max-width: 989px)');
    const seen = { stage: true, builder: true, cart: false };
    let shown = false;
    const update = () => {
      const on = mq.matches && !seen.stage && seen.builder && !seen.cart;
      if (on === shown) return;
      shown = on;
      btn.classList.toggle('is-shown', on);
      btn.tabIndex = on ? 0 : -1;
      this.viewer.keepAlive(on);
    };
    btn.tabIndex = -1;
    new IntersectionObserver((entries) => { seen.stage = entries[0].isIntersecting; update(); }, { threshold: 0.15 }).observe(stage);
    // Hide while the Add to bag button is in the band of the screen the mirror occupies.
    let queued = false;
    const checkCart = () => {
      queued = false;
      if (!add) return;
      const top = parseFloat(getComputedStyle(btn).top) || 0, bottom = top + btn.offsetHeight + 16, r = add.getBoundingClientRect();
      const cart = r.bottom > top - 16 && r.top < bottom;
      if (cart !== seen.cart) { seen.cart = cart; update(); }
    };
    const onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(checkCart); } };
    this.mirrorAbort = new AbortController();
    window.addEventListener('scroll', onScroll, { passive: true, signal: this.mirrorAbort.signal });
    window.addEventListener('resize', onScroll, { passive: true, signal: this.mirrorAbort.signal });
    // The builder is taller than the screen, so any overlap counts as "in the builder".
    new IntersectionObserver((entries) => { seen.builder = entries[0].isIntersecting; update(); }).observe(this);
    mq.addEventListener('change', update);
    this.viewer.onFrame = (src) => {
      if (!shown) return;
      const side = Math.min(src.width, src.height) * 0.86;
      ctx.clearRect(0, 0, 420, 420);
      ctx.drawImage(src, (src.width - side) / 2, (src.height - side) / 2, side, side, 0, 0, 420, 420);
    };
    btn.addEventListener('click', (e) => {
      if (resized || e.target.closest('.cj-mirror__grip')) return;
      stage.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    });
  }

  #bindStage() {
    // Zoom buttons aim at the stones: the top of a ring, the middle of everything else.
    const aim = this.kind === 'ring' ? 0.62 : 0;
    const zIn = this.querySelector('[data-zoom="in"]'), zOut = this.querySelector('[data-zoom="out"]');
    // necklace / bracelet: glide toward the front stones (their centre is empty space)
    const zoomF = (f) => { const v = this.viewer; if (!v) return; if (!(v.zoomOn && v.zoomOn(v.zoom * f, 'zoomAim'))) v.zoomBy(f, 0, aim); };
    zIn?.addEventListener('click', () => zoomF(1.6));
    zOut?.addEventListener('click', () => zoomF(1 / 1.6));
    const zRange = this.querySelector('[data-zoom-range]');
    zRange?.addEventListener('input', () => { const v = this.viewer; if (!v) return; if (!(v.zoomOn && v.zoomOn(Number(zRange.value), 'zoomAim'))) v.zoomTo(Number(zRange.value), 0, aim); });
    this.syncZoom = (z) => {
      if (zIn) zIn.disabled = z >= 3.99;
      if (zOut) zOut.disabled = z <= 1;
      if (zRange && document.activeElement !== zRange) zRange.value = String(z);
    };
    this.querySelectorAll('[data-view]').forEach((b) => b.addEventListener('click', () => this.viewer?.view(b.dataset.view)));
    const spin = this.querySelector('[data-spin]');
    // "Move with phone": tilting the device turns the piece (phones/tablets with a motion sensor only)
    const tiltBtn = this.querySelector('[data-tilt]');
    if (tiltBtn && 'DeviceOrientationEvent' in window && matchMedia('(pointer: coarse)').matches) {
      tiltBtn.hidden = false;
      tiltBtn.addEventListener('click', async (e) => {
        e.preventDefault(); e.stopPropagation(); if (!this.viewer) return;
        const on = tiltBtn.getAttribute('aria-pressed') !== 'true';
        if (on && typeof DeviceOrientationEvent.requestPermission === 'function') {
          try { if (await DeviceOrientationEvent.requestPermission() !== 'granted') return; } catch (err) { return; }
        }
        this.viewer.tilt(on);
        if (on) { this.userSpin = false; this.viewer.spin(false); this.#syncSpin(); }
        tiltBtn.setAttribute('aria-pressed', String(on));
        tiltBtn.querySelector('span').textContent = on ? 'Stop moving' : 'Move with phone';
      });
    }
    // the shopper's choice is the source of truth: temporary pauses (engraving close-ups) never override it
    spin?.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      if (!this.viewer) return;
      if (this.engraveTimer) { clearTimeout(this.engraveTimer); this.engraveTimer = 0; }
      this.userSpin = !(this.userSpin ?? this.viewer.spinning);
      this.viewer.spin(this.userSpin); this.#syncSpin();
    });
  }

  #syncSpin() {
    const spin = this.querySelector('[data-spin]'); if (!spin || !this.viewer) return;
    const on = this.userSpin ?? this.viewer.spinning;
    spin.setAttribute('aria-pressed', String(on));
    spin.querySelector('span').textContent = on ? 'Pause rotation' : 'Rotate';
  }

  async #startViewer() {
    const X = window.CJ3D;
    this.dims = X.dims;
    try {
      while (!window.CJView) await new Promise((r) => setTimeout(r, 50));
      this.viewer = window.CJView.mountViewer(this.querySelector('[data-canvas]'), { kind: this.kind, c: this.state, spin: this.config.autoRotate, zoom: true });
      this.viewer.onStats = (st) => this.#renderStats(st);
      this.viewer.onZoom = this.syncZoom;
      this.#mountMirror();
      this.syncZoom(1);
      const spin = this.querySelector('[data-spin]');
      if (spin) {
        spin.setAttribute('aria-pressed', String(this.viewer.spinning));
        spin.querySelector('span').textContent = this.viewer.spinning ? 'Pause rotation' : 'Rotate';
      }
      this.querySelector('[data-stage-msg]').hidden = true;
      this.querySelector('.cj-stage').classList.add('is-ready');
      this.querySelectorAll('[data-photos], [data-hand-photo]').forEach((b) => (b.disabled = false));
    } catch (e) {
      this.#stageFailed();
    }
    this.#refresh();
  }

  /** The shot list for this design: hero, top and side, plus the engraving when there is one. */
  #shotList() {
    const s = this.state, X = window.CJ3D, views = X.VIEWS[this.kind] || X.VIEWS.ring;
    const shots = [
      { id: 'hero', label: 'Hero', view: views.angled, search: true },
      { id: 'top', label: 'From above', view: views.top },
      { id: 'side', label: 'Side profile', view: views.side },
    ];
    if (s.engraving && views.inside) shots.push({ id: 'inside', label: 'Your engraving', view: views.inside });
    if (s.clasp && views.clasp) shots.push({ id: 'clasp', label: 'Your clasp engraving', view: views.clasp });
    return shots;
  }

  #describe() {
    const s = this.state, col = find(COLORS, s.color).name.toLowerCase();
    const metal = metalName(s);
    if (this.kind === 'ring') return `${s.carat} ct ${s.shape.toLowerCase()} ${find(SETTINGS, s.setting).name.toLowerCase()} ring, ${metal}`;
    if (this.kind === 'eternity') return `${s.stone} mm ${s.shape.toLowerCase()} ${s.coverage === 'half' ? 'half ' : ''}eternity band, ${metal}`;
    if (this.kind === 'tennisNecklace') return `${s.stone} mm tennis necklace, ${s.nlength} in, ${metal}`;
    if (this.kind === 'tennisBracelet') return `${s.stone} mm tennis bracelet, ${s.length} in, ${metal}`;
    return `${s.carat} ct ${s.shape.toLowerCase()} ${s.studset === 'halo' ? 'halo ' : ''}studs, ${metal}`;
  }

  /** Renders one studio photograph: transparent 3D render composited on a soft backdrop with a caption. */
  #shoot(engine, glCanvas, shot, size) {
    let rx = shot.view[1], ry = shot.view[0];
    if (shot.search) {
      // Nudge the angle a little to catch the moment the facets flash brightest (scored at a tiny size).
      engine.size(160, 160);
      let best = -1, bestRy = ry;
      for (let i = -6; i <= 6; i++) {
        const t = ry + i * 0.035;
        engine.draw(rx, t);
        const sc = engine.sparkle();
        if (sc > best) { best = sc; bestRy = t; }
      }
      ry = bestRy;
      engine.size(size, size);
    }
    engine.draw(rx, ry);
    const out = document.createElement('canvas');
    out.width = out.height = size;
    const x = out.getContext('2d');
    const bg = x.createLinearGradient(0, 0, 0, size);
    bg.addColorStop(0, '#FBFAF8'); bg.addColorStop(1, '#ECEEF2');
    x.fillStyle = bg; x.fillRect(0, 0, size, size);
    const glow = x.createRadialGradient(size / 2, size * 0.42, 0, size / 2, size * 0.42, size * 0.55);
    glow.addColorStop(0, 'rgba(255,255,255,0.95)'); glow.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = glow; x.fillRect(0, 0, size, size);
    // Crop in so the piece fills the frame, leaving room for the caption.
    const sw = glCanvas.width, inset = sw * 0.06, art = size * 0.86, off = (size - art) / 2;
    x.drawImage(glCanvas, inset, inset * 0.6, sw - inset * 2, sw - inset * 2, off, size * 0.015, art, art);
    // caption
    const font = getComputedStyle(this).fontFamily || 'sans-serif';
    x.textAlign = 'center';
    this.#stampLogo(x, size, size * 0.948, size * 0.3);
    x.fillStyle = 'rgba(27,45,74,0.72)';
    x.font = `400 ${Math.round(size * 0.017)}px ${font}`;
    x.fillText(this.#describe(), size / 2, size * 0.978);
    return out;
  }

  /** Loads the JAQUE HUÉ logo once (same-origin theme asset, so canvases stay exportable). */
  #loadLogo() {
    if (!this.logoPromise) {
      this.logoPromise = new Promise((resolve) => {
        if (!this.config.logo) return resolve(null);
        const img = new Image();
        // CORS-clean even when the theme CDN is on another domain, so photos can still be saved.
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = this.config.logo;
      });
    }
    return this.logoPromise;
  }

  /** Draws the logo centered with its baseline at y; falls back to the brand name in text. */
  #stampLogo(x, size, y, width) {
    const logo = this.logoImg;
    if (logo) {
      const h = width * (logo.naturalHeight / logo.naturalWidth);
      x.drawImage(logo, (size - width) / 2, y - h, width, h);
      return;
    }
    x.fillStyle = '#8D662A';
    x.font = `500 ${Math.round(size * 0.022)}px Georgia, serif`;
    if ('letterSpacing' in x) x.letterSpacing = `${Math.round(size * 0.004)}px`;
    x.fillText((this.config.brand || 'JAQUE HUÉ').toUpperCase(), size / 2, y);
    if ('letterSpacing' in x) x.letterSpacing = '0px';
  }

  /** Adds a soft light band and the logo to the bottom of an AI photo. Returns a JPEG data URL. */
  async #brandPhoto(dataUrl) {
    const img = await new Promise((resolve, reject) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = reject; i.src = dataUrl; });
    const size = img.naturalWidth, h = img.naturalHeight;
    const c = document.createElement('canvas'); c.width = size; c.height = h;
    const x = c.getContext('2d');
    x.drawImage(img, 0, 0);
    const band = x.createLinearGradient(0, h * 0.8, 0, h);
    band.addColorStop(0, 'rgba(255,255,255,0)'); band.addColorStop(1, 'rgba(255,255,255,0.82)');
    x.fillStyle = band; x.fillRect(0, h * 0.8, size, h * 0.2);
    x.textAlign = 'center';
    this.#stampLogo(x, size, h * 0.965, size * 0.28);
    return c.toDataURL('image/jpeg', 0.92);
  }

  #photoDialog() {
    if (this.photoDlg) return this.photoDlg;
    const d = document.createElement('dialog');
    d.className = 'cj-photos';
    d.setAttribute('aria-labelledby', `${this.id}-photos-title`);
    d.innerHTML =
      `<div class="cj-photos__head"><h2 id="${this.id}-photos-title">Photos of your design</h2>` +
      '<button type="button" class="cj-photos__x" data-close aria-label="Close">' +
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>' +
      '<p class="cj-photos__sub" data-photos-sub></p>' +
      '<p class="cj-photos__status" data-photos-status role="status"></p>' +
      '<div class="cj-photos__grid" data-photos-grid></div>' +
      '<div class="cj-photos__foot"><button type="button" class="button button-secondary" data-share hidden>Share</button>' +
      '<button type="button" class="button" data-add-from-photos>Add this design to bag</button></div>';
    d.addEventListener('click', (ev) => {
      if (ev.target === d || ev.target.closest('[data-close]')) d.close();
      if (ev.target.closest('[data-add-from-photos]')) { d.close(); this.querySelector('[data-add]').click(); }
      if (ev.target.closest('[data-share]')) this.#sharePhotos();
    });
    this.appendChild(d);
    this.photoDlg = d;
    return d;
  }

  /** The off-screen studio engine, recreated whenever the browser has taken its GPU context back. */
  #studio() {
    if (this.photoEngine && this.photoEngine.lost()) this.#dropStudio();
    if (!this.photoEngine) {
      this.photoCanvas = document.createElement('canvas');
      this.photoEngine = window.CJ3D.Engine(this.photoCanvas, false, true);
    }
    return this.photoEngine;
  }

  #dropStudio() {
    try { this.photoEngine?.dispose(); } catch (e) { /* already gone */ }
    this.photoEngine = null;
    this.photoCanvas = null;
  }

  /** Shows one finished photo in its slot and keeps it for sharing. */
  #placePhoto(grid, sh, url, slug, px) {
    const bin = atob(url.split(',')[1]), bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const name = `jaque-hue-${slug}-${sh.id}.jpg`;
    this.photoFiles.push(new File([bytes], name, { type: 'image/jpeg' }));
    const fig = grid.querySelector(`[data-shot="${sh.id}"]`);
    fig.classList.remove('is-loading');
    fig.querySelector('.cj-photo__img').innerHTML = `<img src="${url}" alt="${sh.label}: ${this.#describe()}" width="${px}" height="${px}">`;
    const cap = fig.querySelector('figcaption');
    cap.querySelector('a')?.remove();
    cap.insertAdjacentHTML('beforeend', `<a href="${url}" download="${name}">Download</a>`);
  }

  async #renderShots(shots, grid, slug, want) {
    const eng = this.#studio();
    const px = Math.min(want, eng.maxSize());
    eng.pixelRatio(1);
    eng.size(px, px);
    eng.shadow(true);
    eng.show(this.kind, Object.assign({}, window.CJView.DEFAULTS, this.state, { size: this.state.size || '7' }));
    for (const sh of shots) {
      await new Promise((r) => setTimeout(r, 30));
      if (eng.lost()) throw new Error('GPU context lost');
      const photo = this.#shoot(eng, this.photoCanvas, sh, px);
      // Synchronous encode: dependable everywhere (async toBlob can stall in some browsers).
      const url = photo.toDataURL('image/jpeg', 0.9);
      if (url.length < 5000) throw new Error('blank render');
      this.#placePhoto(grid, sh, url, slug, px);
    }
  }

  /** Last resort: photograph the live 3D viewer itself from each angle. */
  async #snapshotFallback(shots, grid, slug, err) {
    console.error('[cj-photos] studio unavailable, using the live viewer', err);
    if (!this.viewer) throw err;
    const views = window.CJ3D.VIEWS[this.kind] || window.CJ3D.VIEWS.ring;
    const wasSpinning = this.viewer.spinning;
    this.viewer.spin(false);
    for (const sh of shots) {
      const name = Object.keys(views).find((k) => views[k] === sh.view) || 'angled';
      this.viewer.view(name);
      await new Promise((r) => setTimeout(r, 900));
      const snap = this.viewer.snapshot(900, '#F7F5F2');
      // Add the brand caption on top of the snapshot.
      const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = snap; });
      const c = document.createElement('canvas'); c.width = c.height = 900;
      const x = c.getContext('2d');
      x.drawImage(img, 0, 0);
      x.textAlign = 'center';
      this.#stampLogo(x, 900, 900 * 0.955, 900 * 0.3);
      this.#placePhoto(grid, sh, c.toDataURL('image/jpeg', 0.9), slug, 900);
    }
    this.viewer.spin(this.userSpin ?? wasSpinning);
  }

  async #openPhotos() {
    const d = this.#photoDialog(), grid = d.querySelector('[data-photos-grid]'), status = d.querySelector('[data-photos-status]');
    const btn = this.querySelector('[data-photos]');
    d.querySelector('[data-photos-sub]').textContent = this.#describe();
    grid.innerHTML = '';
    this.photoFiles = [];
    const shots = this.#shotList();
    shots.forEach((sh) => {
      const fig = document.createElement('figure');
      fig.className = 'cj-photo is-loading';
      fig.innerHTML = `<div class="cj-photo__img"></div><figcaption><span>${sh.label}</span></figcaption>`;
      fig.dataset.shot = sh.id;
      grid.appendChild(fig);
    });
    status.textContent = `Creating ${shots.length} photos…`;
    if (!d.open) d.showModal();
    btn.setAttribute('aria-busy', 'true');
    try {
      await window.CJReady;
      const X = window.CJ3D;
      this.logoImg = await this.#loadLogo();
      const slug = this.#describe().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      // Phones get a lighter render; if a size fails, rebuild the engine and try smaller.
      const phone = matchMedia('(pointer: coarse)').matches || (navigator.deviceMemory || 8) <= 4;
      const sizes = phone ? [1080, 720] : [1600, 1000];
      let done = false, lastErr;
      for (const want of sizes) {
        try {
          await this.#renderShots(shots, grid, slug, want);
          done = true;
          break;
        } catch (err) {
          lastErr = err;
          console.warn('[cj-photos] retrying smaller', err);
          this.#dropStudio();
          this.photoFiles = [];
        }
      }
      if (!done) await this.#snapshotFallback(shots, grid, slug, lastErr);
      status.textContent = 'Your photos are ready. Download them or share them with someone special.';
      const share = d.querySelector('[data-share]');
      share.hidden = !(navigator.canShare && navigator.canShare({ files: this.photoFiles }));
    } catch (err) {
      console.error('[cj-photos]', err);
      status.textContent = 'We couldn’t create photos in this browser. Try again, or use the 3D preview to see your design.';
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }

  /** Ready-made photos of this exact ring design: the 2x2 studio collage and the on-hand photo, when they exist. */
  #realPhotos() {
    const s = this.state, k = this.kind, col = s.color === 'white' ? 'silver' : s.color, sh = String(s.shape).toLowerCase();
    // ring keys are bare (setting-shape-color); the other builders are prefixed with their kind
    const key = k === 'ring' ? `${s.setting}-${sh}-${col}` : k === 'eternity' ? `eternity:${s.coverage}-${sh}-${col}` : k === 'studs' ? `studs:${s.studset}-${sh}-${col}` : `${k}:${s.stone}mm-${col}`;
    const studio = this.config.productLibrary?.[key], hand = this.config.handLibrary?.[key], out = [];
    if (studio) out.push({ url: studio, label: 'Studio views', alt: `${this.#describe()}: three-quarter, top, side and close-up views`, size: 2048, zoom: 3, file: 'studio' });
    if (hand) out.push({ url: hand, label: k === 'ring' || k === 'eternity' ? 'On a hand' : 'Worn', alt: `${this.#describe()} worn`, size: 1024, zoom: 2.2, file: 'on-hand' });
    return out;
  }

  #realDialog() {
    if (this.realDlg) return this.realDlg;
    const d = document.createElement('dialog');
    d.className = 'cj-photos cj-real';
    d.setAttribute('aria-labelledby', `${this.id}-real-title`);
    d.innerHTML =
      `<div class="cj-photos__head"><h2 id="${this.id}-real-title">Real photos</h2>` +
      '<button type="button" class="cj-photos__x" data-close aria-label="Close"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>' +
      '<p class="cj-photos__sub" data-real-sub></p>' +
      '<p class="cj-photos__status">Click or tap a photo to zoom in, then drag to look around.</p>' +
      '<div class="cj-real__grid" data-real></div>' +
      '<p class="cj-fine">Photorealistic images of this design. Your piece is made to the exact carat, prongs, band and size you choose.</p>' +
      '<div class="cj-photos__foot"><button type="button" class="button" data-add-from-real>Add this design to bag</button></div>';
    d.addEventListener('click', (ev) => {
      if (ev.target === d || ev.target.closest('[data-close]')) d.close();
      if (ev.target.closest('[data-add-from-real]')) { d.close(); this.querySelector('[data-add]').click(); }
    });
    this.appendChild(d);
    this.realDlg = d;
    return d;
  }

  #openHand() {
    const photos = this.#realPhotos();
    if (!photos.length) return;
    const d = this.#realDialog(), grid = d.querySelector('[data-real]');
    const slug = this.#describe().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    d.querySelector('[data-real-sub]').textContent = this.#describe();
    grid.classList.toggle('is-single', photos.length === 1);
    grid.innerHTML = photos.map((p) =>
      `<figure class="cj-real__item"><div class="cj-zoom" role="button" tabindex="0" aria-label="Zoom in: ${p.label}" style="--cj-zoom: ${p.zoom * 100}%">` +
      // Show a 1000px copy first; the full-resolution file loads only when the shopper zooms in.
      `<img src="${p.url}${p.url.includes('?') ? '&' : '?'}width=1000" data-full="${p.url}" alt="${p.alt}" width="${p.size}" height="${p.size}" loading="lazy" draggable="false"></div>` +
      `<figcaption><span>${p.label}</span><a href="${p.url}" download="jaque-hue-${slug}-${p.file}.jpg" target="_blank" rel="noopener">Download</a></figcaption></figure>`).join('');
    grid.querySelectorAll('.cj-zoom').forEach((box) => this.#bindZoom(box));
    if (!d.open) d.showModal();
  }

  /** Click or tap zooms into that spot; drag pans with a mouse (touch pans natively); click again zooms out. */
  #bindZoom(box) {
    let drag = null;
    const label = box.getAttribute('aria-label').replace(/^Zoom (in|out): /, '');
    const toggle = (fx, fy) => {
      const on = box.classList.toggle('is-zoomed'), img = box.querySelector('img');
      if (on && img.dataset.full && img.src !== img.dataset.full) img.src = img.dataset.full;
      box.setAttribute('aria-label', `${on ? 'Zoom out' : 'Zoom in'}: ${label}`);
      if (!on) return;
      requestAnimationFrame(() => {
        box.scrollLeft = fx * box.scrollWidth - box.clientWidth / 2;
        box.scrollTop = fy * box.scrollHeight - box.clientHeight / 2;
      });
    };
    box.addEventListener('click', (e) => {
      if (drag && drag.moved) { drag = null; return; }
      drag = null;
      const r = box.getBoundingClientRect();
      toggle((e.clientX - r.left + box.scrollLeft) / box.scrollWidth || 0.5, (e.clientY - r.top + box.scrollTop) / box.scrollHeight || 0.5);
    });
    box.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      toggle(0.5, 0.5);
    });
    box.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || !box.classList.contains('is-zoomed')) return;
      drag = { x: e.clientX, y: e.clientY, l: box.scrollLeft, t: box.scrollTop, moved: false };
    });
    box.addEventListener('pointermove', (e) => {
      if (!drag || e.buttons !== 1) return;
      const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (Math.abs(dx) + Math.abs(dy) > 4) drag.moved = true;
      box.scrollLeft = drag.l - dx;
      box.scrollTop = drag.t - dy;
    });
  }

  async #sharePhotos() {
    try { await navigator.share({ files: this.photoFiles, title: 'My JAQUE HUÉ design', text: this.#describe() }); } catch (e) { /* cancelled */ }
  }

  /** Turns the bracelet so the clasp, where the two letters sit, faces the shopper. */
  #showClasp() {
    if (!this.viewer) return;
    if (this.engraveTimer) clearTimeout(this.engraveTimer);
    else this.wasSpinning = this.userSpin ?? this.viewer.spinning;
    this.viewer.spin(false);
    this.viewer.view('clasp');
    this.engraveTimer = setTimeout(() => { this.engraveTimer = 0; this.viewer.spin(this.userSpin ?? this.wasSpinning); }, 5000);
  }

  /** Turns the piece so the inside of the band, where the engraving sits, faces the shopper. */
  #showEngraving() {
    if (!this.viewer) return;
    if (this.engraveTimer) clearTimeout(this.engraveTimer);
    else this.wasSpinning = this.userSpin ?? this.viewer.spinning;
    this.viewer.spin(false);
    this.viewer.view('inside');
    this.engraveTimer = setTimeout(() => {
      this.engraveTimer = 0;
      this.viewer.spin(this.userSpin ?? this.wasSpinning);
    }, 5000);
  }

  #renderStats(st) {
    const box = this.querySelector('[data-stats]');
    if (!box) return;
    if (!st || !st.totalCt) { box.hidden = true; return; }
    const s = this.state, gold = s.material === 'gold';
    const grams = (st.metalMm3 / 1000) * (gold ? 13.1 : 10.4);
    const rows = [
      ['Center diamond', `${s.carat} ct ${s.shape.toLowerCase()}, ${this.dims(s.shape, s.carat)}`],
      ['Diamonds in total', `${1 + st.accents}${st.accents ? ` (1 center, ${st.accents} accent)` : ''}`],
      ['Total carat weight', `about ${st.totalCt.toFixed(2)} ct`],
      ['Setting height', `${st.heightMm.toFixed(1)} mm above the band`],
      ['Estimated metal weight', `${grams.toFixed(1)} g ${gold ? '14k gold' : 'sterling silver'}`],
      ['Diamond quality', 'Excellent cut, F–G color, VS clarity, IGI graded'],
    ];
    box.querySelector('dl').innerHTML = rows.map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join('');
    box.hidden = false;
  }

  #stageFailed() {
    this.querySelector('.cj-stage').classList.add('is-ready');
    const msg = this.querySelector('[data-stage-msg]');
    msg.hidden = false;
    msg.textContent = 'The 3D preview isn’t available in this browser. Your choices still work.';
  }

  /** A transparent render of the shopper's ring, seen the way it sits in the box slot (rings only). */
  #ringForBox() {
    if (this.kind !== 'ring' || !window.CJ3D) return null;
    try {
      const eng = this.#studio(), px = Math.min(700, eng.maxSize());
      eng.pixelRatio(1);
      eng.size(px, px);
      eng.shadow(false);
      eng.show('ring', Object.assign({}, window.CJView.DEFAULTS, this.state, { size: this.state.size || '6', engraving: '' }));
      eng.draw(0.28, 0.32);
      const src = this.photoCanvas, c = document.createElement('canvas');
      c.width = src.width; c.height = src.height;
      const x = c.getContext('2d');
      x.drawImage(src, 0, 0);
      const d = x.getImageData(0, 0, c.width, c.height).data;
      let x0 = c.width, y0 = c.height, x1 = 0, y1 = 0;
      for (let y = 0; y < c.height; y += 2) for (let xx = 0; xx < c.width; xx += 2) {
        if (d[(y * c.width + xx) * 4 + 3] > 12) { if (xx < x0) x0 = xx; if (xx > x1) x1 = xx; if (y < y0) y0 = y; if (y > y1) y1 = y; }
      }
      eng.shadow(true);
      if (x1 <= x0) return null;
      const out = document.createElement('canvas'), w = x1 - x0 + 4, h = y1 - y0 + 4;
      out.width = w; out.height = h;
      out.getContext('2d').drawImage(c, x0 - 2, y0 - 2, w, h, 0, 0, w, h);
      return out.toDataURL('image/png');
    } catch (e) {
      return null;
    }
  }

  /** Opens the Keepsake Studio for this design; resolves once the shopper saves or skips. */
  #openKeepsake(opts = {}) {
    const K = window.CJKeepsake;
    if (!K) return Promise.resolve();
    this.box.visited = true;
    return new Promise((resolve) => {
      K.open({
        mode: 'select',
        ring: this.#ringForBox(),
        ringState: Object.assign({}, this.state, { engraving: '', kind: this.kind }),
        view: '3d',
        values: this.box.values || undefined,
        goLabel: opts.goLabel,
        skipLabel: opts.skipLabel,
        embed: opts.embed,
        onSave: (v) => { this.box.values = v; this.#refresh(); resolve('save'); },
        onSkip: () => resolve('skip'),
      });
      if (opts.embed) this.#onStudioClose(resolve);
    });
  }

  /** Embedded studio closed with its X (not saved or skipped): resolve as "back". */
  #onStudioClose(resolve) {
    const d = document.querySelector('dialog.ks'); if (!d) return;
    d.addEventListener('close', () => setTimeout(() => resolve('back'), 0), { once: true });
  }

  /** Opens the Keepsake Studio on the rest tray; resolves once the shopper saves or skips. */
  #openTray(opts = {}) {
    const K = window.CJKeepsake;
    if (!K?.items?.tray) return Promise.resolve();
    this.tray.visited = true;
    return new Promise((resolve) => {
      K.open({
        mode: 'select', item: 'tray', view: '3d',
        ringState: Object.assign({}, this.state, { engraving: '', kind: this.kind }),
        values: this.tray.values || this.box.values || undefined,
        goLabel: opts.goLabel, skipLabel: opts.skipLabel, embed: opts.embed,
        onSave: (v) => { this.tray.values = v; this.#refresh(); resolve('save'); },
        onSkip: () => resolve('skip'),
      });
      if (opts.embed) this.#onStudioClose(resolve);
    });
  }

  async #addToCart() {
    const btn = this.querySelector('[data-add]'), errEl = this.querySelector('[data-add-error]');
    errEl.hidden = true;
    if (SIZED[this.kind] && !this.state.size) {
      const sizePanel = this.sizeSelect.closest('details');
      if (sizePanel) sizePanel.open = true;
      if (this.wizShow && sizePanel) { const all = [...this.querySelectorAll('[data-steps] > .cj-step-panel')]; this.wizShow(all.indexOf(sizePanel), true); }
      this.sizeError.hidden = false;
      this.sizeSelect.classList.add('is-invalid');
      this.sizeSelect.focus();
      return;
    }
    const variant = this.variant;
    if (!variant) return;
    const K = window.CJKeepsake, withBox = Boolean(K && this.box.include);
    if (withBox && !this.box.visited) await this.#openKeepsake({ goLabel: 'Save and add to bag', skipLabel: 'Add to bag without engraving' });
    const withTray = Boolean(K?.items?.tray && this.tray.include);
    if (withTray && !this.tray.visited) await this.#openTray({ goLabel: 'Save and add to bag', skipLabel: 'Add the tray without engraving' });
    const blank = { l1: '', l2: '', font: 'classic', motif: 'none' };
    if (K) K.markDone();
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: [{ merchandiseId: String(variant.id), quantity: 1 }, ...(withBox ? [{ merchandiseId: String(K.variantId), quantity: 1 }] : []), ...(withTray ? [{ merchandiseId: K.items.tray.variantId, quantity: 1 }] : [])],
      promise: deferred.promise,
    }));

    const grp = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    // keep a snapshot of the shopper's own 3D design (uniform backdrop) so the cart can show it as the thumbnail
    let renderProp = {};
    try { const snap = this.viewer && this.viewer.snapshot(360, '#EEEDEB', 'angled'); if (snap) { const rid = Date.now().toString(36) + Math.random().toString(36).slice(2, 7); localStorage.setItem('jq-render-' + rid, snap); renderProp = { _render: rid }; } } catch (e) { /* storage full or no viewer */ }
    // and a full-size copy saved with the order (hidden from the shopper, shown on the order in admin)
    try {
      const big = this.viewer && this.viewer.snapshot(1200, '#EEEDEB', 'angled');
      if (big) {
        const blob = await (await fetch(big)).blob(), fd = new FormData();
        fd.append('file', blob, 'design-render.jpg');
        const ctl = new AbortController(), t = setTimeout(() => ctl.abort(), 6000);
        const r = await fetch(UPLOADS, { method: 'POST', body: fd, signal: ctl.signal }); clearTimeout(t);
        const j = await r.json(); if (r.ok && j.url) renderProp['_Design render'] = j.url;
      }
    } catch (e) { /* the order still goes through without the saved render */ }
    // and the 3D model itself (GLB), so the workshop gets the exact design
    try {
      const glb = await this.#exportGlb();
      if (glb) { const fd = new FormData(); fd.append('file', new Blob([glb], { type: 'model/gltf-binary' }), 'design.glb');
        const ctl = new AbortController(), t = setTimeout(() => ctl.abort(), 12000);
        const r = await fetch(UPLOADS, { method: 'POST', body: fd, signal: ctl.signal }); clearTimeout(t);
        const j = await r.json(); if (r.ok && j.url) renderProp['_3D model'] = j.url; }
    } catch (e) { /* the order still goes through without the 3D file */ }

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: variant.id, quantity: 1, properties: Object.assign(this.#properties(), renderProp, { _g: grp, _design: JSON.stringify({ s: this.state, box: { include: withBox, values: this.box.values }, tray: { include: withTray, values: this.tray.values } }) }) },
            ...(withBox ? [{ id: Number(K.variantId), quantity: 1, properties: Object.assign(K.properties(this.box.values || blank), { 'For': this.#describe(), _g: grp }) }] : []),
            ...(withTray ? [{ id: Number(K.items.tray.variantId), quantity: 1, properties: Object.assign(K.properties(this.tray.values || blank), { 'For': this.#describe(), _g: grp }) }] : []),
          ],
          sections: sectionIds.join(','),
        }),
      });
      const data = await res.json();
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const detail = { items: cart.items, source: 'cj-builder', sourceId: this.id, itemCount: 1 + (withBox ? 1 : 0) + (withTray ? 1 : 0), productId: String(this.config.productId), sections: data.sections, didError: Boolean(data.status) };
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail });
      if (data.status) {
        this.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID', detail: { description: data.description } }));
        throw new Error(data.description || data.message);
      }
      if (this.editKey) {
        const updates = {}; cart.items.forEach((i) => { if (i.key === this.editKey || (this.editGroup && i.properties && i.properties._g === this.editGroup)) updates[i.key] = 0; });
        if (Object.keys(updates).length) await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
        this.editKey = null; history.replaceState(history.state, '', location.pathname + location.search.replace(/([?&])edit=[^&]*&?/, '$1').replace(/[?&]$/, ''));
      }
      if (window.JQ) { await window.JQ.refreshCart(); window.JQ.openCart(true); } else if (!document.querySelector('cart-drawer-component')) location.href = Theme.routes.cart_url;
    } catch (e) {
      errEl.textContent = e.message || 'Something went wrong. Please try again.';
      errEl.hidden = false;
      deferred.reject?.(e);
    } finally {
      btn.removeAttribute('aria-busy');
      btn.disabled = !(this.variant && this.variant.available);
    }
  }
}

if (!customElements.get('cj-builder')) customElements.define('cj-builder', CjBuilder);
